import _defaultTo from 'lodash/defaultTo';
import _findKey from 'lodash/findKey';
import _forEach from 'lodash/forEach';
import _get from 'lodash/get';
import _isEqual from 'lodash/isEqual';
import _isNil from 'lodash/isNil';
import _last from 'lodash/last';
import _map from 'lodash/map';
import _min from 'lodash/min';
import _orderBy from 'lodash/orderBy';
import _size from 'lodash/size';
import _sortBy from 'lodash/sortBy';
import _split from 'lodash/split';
// Serialization
import { deserialize, serialize } from 'serializr';
import { planningSchema } from './schemas';
// Custom
import {
  dateFormatter, dateGenerator, dateGetter, dateQuery, localeData, timezoneUtils,
} from '../utils/DateUtils';

/**
* Serialization
*/
const serializerKey = 'planning';

const deserializePlanning = (data) => deserialize(planningSchema, data);

const serializePlanning = ({
  entityId, name, slots, enabled,
}) => ({
  [serializerKey]: {
    ...serialize(planningSchema, {
      entity_id: _defaultTo(entityId, null),
      name: _defaultTo(name, null),
      ...(slots && { slots }),
      ...(!_isNil(enabled) && { enabled }),
    }),
  },
});

/**
* Utils
*/
const sortPlannings = (plannings, fields = ['name'], dirs = ['asc']) => (_orderBy(plannings, fields, dirs));

const planningTitle = (planningData) => _get(planningData, 'name', '');

/**
* Utils function for calendar management
*/
const MAX_SLOT_END_MINUTES = 7 * 60 * 24;

// Timezone used for dates manipulating
let workingTimezone;
const setWorkingTimezone = (timezone) => {
  workingTimezone = timezone;
};

// End event trick: we use events with 23:59 of the previous day when the event
// finishes at 00:00 of a day to avoid displaying the end of the event on the next day
// This function performs the reverse operation
const computeEventEnd = (end) => {
  if (dateGetter.hours(end) === 23 && dateGetter.minutes(end) === 59) {
    // Add one minute to retrieve the real time
    return dateGenerator.dateAdder(end, { minutes: 1 });
  }
  return end;
};

// Week calendar start date
// It's a monday, defined arbitrary, never displayed, from which all week periods are based
// Note: not defined as constant to avoid mutation
const weekStartDate = () => dateGenerator.dateFromTimezone('2017-11-06T00:00:00.000Z', workingTimezone);

const weekScrollToTime = (date = {}) => dateGenerator.dateSubtractor(date, { hours: _min([7, dateGetter.hours(date)]) });

const weekEventFormatter = (start, end) => {
  let formattedStart = '';
  if (start) {
    formattedStart = dateFormatter.toLocalizedTime(start);
  }
  let formattedEnd = '';
  if (end) {
    formattedEnd = dateFormatter.toLocalizedTime(computeEventEnd(end));
  }
  return `${formattedStart} - ${formattedEnd}`;
};

// Create a localized days list
// Note that if `weekdays` param is ommitted, first day of week must been set to '1' (monday) before calling this function,
// otherwise unexpected results could occur
const weekDaysLocalized = (weekdays = localeData.weekdays()) => (
  _map(weekdays, (day, index) => ({
    id: index,
    label: day,
  }))
);

// Warning: need to clone weekStartDate() as dateAdder mutates initial object!
const weekStartDatePlusMinutes = (minutes) => dateGenerator.dateAdder(weekStartDate(), { minutes });

// isoWeekday returns (1 = Monday, ..., 6 = Saturday, 7 = Sunday)
// OPAC storage needs (0 = Monday, 1 = Tuesday, ..., 6 = Sunday)
const getDayId = (time) => dateGetter.isoWeekday(time) - 1;

const daysId = {
  mo: 0,
  tu: 1,
  we: 2,
  th: 3,
  fr: 4,
  sa: 5,
  su: 6,
};

const dayIdIsWeekend = (dayId) => (dayId > 4);

// For next functions:
// An event is an object {id, start, end} where `start` and `end` are dates => used to display slots in calendar
// A slot is an object {start: {day_of_week, time}, end: {day_of_week, time}} => format in database (time is hh:mm:ss format)
// A period is an object {start: {dayId, time}, end: {dayId, time}} => used in edition popup (time is a Date object)
// A tempSlot is an object {start: {dayId, time}, end: {dayId, time}} => used for internal manipulations (time is hh:mm:ss format)

// Convert a tempSlot into an event
const tempSlotToEvent = ({ start, end, ...other }) => {
  const startTimeParts = _split(start.time, ':');
  const startDate = dateGenerator.date(dateGenerator.now(), { hour: startTimeParts[0], minutes: startTimeParts[1] });
  const startMinutes = start.dayId * 60 * 24 + dateGetter.minutesQuantity(startDate);

  const endTimeParts = _split(end.time, ':');
  const endDate = dateGenerator.date(dateGenerator.now(), { hour: endTimeParts[0], minutes: endTimeParts[1] });
  const endMinutes = end.dayId * 60 * 24 + dateGetter.minutesQuantity(endDate);

  // Get the offset of the working timezone
  const timezoneOffset = timezoneUtils.offset(workingTimezone, weekStartDate());

  return {
    start: weekStartDatePlusMinutes(startMinutes + timezoneOffset),
    end: weekStartDatePlusMinutes(endMinutes + timezoneOffset),
    ...other,
  };
};

// Convert an event into a period
const eventToPeriod = ({ start, end }) => {
  // Treat end : if end time is 23:59, display 00:00 of the next day
  const usedEnd = computeEventEnd(end);

  return ({
    start: {
      dayId: getDayId(start),
      time: start,
    },
    end: {
      dayId: getDayId(usedEnd),
      time: usedEnd,
    },
  });
};

// Convert a period into an event
const periodToEvent = ({ start, end }) => {
  const usedEnd = { ...end };
  // If end is 00:00, remove one minute (to avoid displaying event on next day)
  if (dateGetter.hours(end.time) === 0 && dateGetter.minutes(end.time) === 0) {
    usedEnd.dayId = end.dayId - 1;
    usedEnd.time = dateGenerator.dateSubtractor(end.time, { minutes: 1 });
  }

  // Create a temp slot
  const tempSlot = {
    start: {
      dayId: start.dayId,
      time: dateFormatter.toServerFormatTimeOnly(start.time),
    },
    end: {
      dayId: usedEnd.dayId,
      time: dateFormatter.toServerFormatTimeOnly(usedEnd.time),
    },
  };

  // Convert into event
  return tempSlotToEvent(tempSlot);
};

// Convert a period for beginning at a given dayId
const periodToPeriodInDay = ({ start, end }, dayId) => ({
  start: {
    dayId: start.dayId + dayId,
    time: start.time,
  },
  end: {
    dayId: (end.dayId + dayId > daysId.su) ? daysId.su : end.dayId + dayId,
    time: (end.dayId + dayId > daysId.su) ? weekStartDatePlusMinutes(MAX_SLOT_END_MINUTES - 1 + timezoneUtils.offset(workingTimezone, weekStartDate())) : end.time,
  },
});

// Events utils
const eventStartsInWeekend = (event) => {
  const period = eventToPeriod(event);
  return dayIdIsWeekend(period.start.dayId);
};

const eventStartsInDay = (event, dayId) => {
  const period = eventToPeriod(event);
  return period.start.dayId === dayId;
};

// Parse events to check conflicts (intersections)
const sanitizeEvents = (events) => {
  // Date query methods
  const { isSameOrBefore, isSameOrAfter, isAfter } = dateQuery;

  // First sort events by start
  const sortedEvents = _sortBy(events, 'start');
  // Check overlap between events and create a final array of events
  const finalEvents = [];
  _forEach(sortedEvents, (event, index) => {
    if (index === 0) {
      finalEvents.push(event);
    } else {
      const prevIndex = finalEvents.length - 1;
      const prevEvent = _last(finalEvents);
      const { id: prevId, start: prevStart, end: prevEnd } = prevEvent;
      const { id, start, end } = event;
      const usedId = _min([id, prevId]);

      if (isSameOrBefore(start, prevStart) && isAfter(end, prevStart)) {
        // Start of event is before start of previous event, and end if after => update previous
        finalEvents[prevIndex] = { id: usedId, start, end: dateGenerator.date(dateGetter.max(prevEnd, end)) };
      } else if (isSameOrAfter(start, prevStart) && isSameOrBefore(start, prevEnd)) {
        // Start of event is between start and end of previous event => merge them
        finalEvents[prevIndex] = { id: usedId, start: prevStart, end: dateGenerator.date(dateGetter.max(prevEnd, end)) };
      } else if (
        dateGetter.hours(prevEnd) === 23 && dateGetter.minutes(prevEnd) === 59
        && dateGetter.hours(start) === 0 && dateGetter.minutes(start) === 0
        && dateGetter.isoWeekday(start) === dateGetter.isoWeekday(prevEnd) + 1
      ) {
        // End of previous event is 23:59 and start of event is 00:00 the days just after => union them
        finalEvents[prevIndex] = { id: usedId, start: prevStart, end };
      } else {
        finalEvents.push(event);
      }
    }
  });

  return finalEvents;
};

// Convert slots into events (used when displaying a planning from server data)
const slotsToEvents = (slots, dayOfWeekKey = 'day_of_week') => _map(slots, ({ start, end }, index) => {
  const { [dayOfWeekKey]: startDayOfWeek, time: startTime } = start;
  const { [dayOfWeekKey]: endDayOfWeek, time: endTime } = end;

  // If end is Monday/00:00:00, use Sunday/23:59:00 for event
  const tempSlotEnd = (endDayOfWeek === 'mo' && endTime === '00:00:00' ? {
    dayId: daysId.su,
    time: '23:59:00',
  } : {
    dayId: daysId[endDayOfWeek],
    time: endTime,
  });
  // Create a temp slot
  const tempSlot = {
    start: {
      dayId: daysId[startDayOfWeek],
      time: startTime,
    },
    end: { ...tempSlotEnd },
    id: index + 1,
  };

  // Convert into event
  return tempSlotToEvent(tempSlot);
});

// Convert events into slots (used to serialize slots before sending to server)
const eventsToSlots = (events) => _map(events, ({ start, end }) => {
  // Treat end : if end time is 23:59, send 00:00 of the next day
  const usedEnd = computeEventEnd(end);

  return ({
    start: {
      day_of_week: _findKey(daysId, (dayId) => (dayId === getDayId(start))),
      time: dateFormatter.toServerFormatTimeOnly(start),
    },
    end: {
      day_of_week: _findKey(daysId, (dayId) => (dayId === getDayId(usedEnd))),
      time: dateFormatter.toServerFormatTimeOnly(usedEnd),
    },
  });
});

// Returns if two arrays of slots are equals
const slotsAreEqual = (slots1, slots2) => {
  if (_size(slots1) !== _size(slots2)) {
    return false;
  }

  // Sort slots by their start
  const sortedSlots1 = _sortBy(slots1, ['start.dayId', 'start.time']);
  const sortedSlots2 = _sortBy(slots2, ['start.dayId', 'start.time']);

  // Check equality of each slots
  let areEqual = true;
  _forEach(sortedSlots1, (slot, index) => {
    if (!_isEqual(slot, sortedSlots2[index])) {
      areEqual = false;
      return false;
    }
  });

  return areEqual;
};

/**
* Model
*/
const planning = {
  // Name
  name: 'planning',

  // Serialization
  deserialize: deserializePlanning,
  serialize: serializePlanning,

  // Sort elements
  sortElements: sortPlannings,

  // Utils
  title: planningTitle,
  // Calendar utils
  maxSlotEndMinutes: MAX_SLOT_END_MINUTES,
  setWorkingTimezone,
  weekStartDate,
  weekScrollToTime,
  weekEventFormatter,
  weekDaysLocalized,
  daysId,
  eventToPeriod,
  periodToEvent,
  periodToPeriodInDay,
  eventStartsInWeekend,
  eventStartsInDay,
  sanitizeEvents,
  slotsToEvents,
  eventsToSlots,
  slotsAreEqual,

  // List features
  listFeatures: ({ withEntity } = {}) => ({
    // Default list param (not used)
    defaultParams: {
      pagination: {
        page: 1,
        pageSize: 20,
      },
      sort: [],
    },

    // Columns
    columnsQuantity: () => ({
      xs: 2,
      sm: 3,
      md: 3 + (withEntity | 0),
      lg: 3 + (withEntity | 0),
      xl: 3 + (withEntity | 0),
    }),
    columns: [
      {
        key: 'name',
        labelId: 'field.name',
        field: 'name',
        sortable: false,
      },
      {
        key: 'entity',
        hidden: !withEntity,
        labelId: 'field.entity',
        field: 'entity',
        sortable: false,
        className: 'hidden-sm-down',
      },
      {
        key: 'active',
        labelId: 'field.activeM',
        field: 'active',
        sortable: false,
        className: 'table-content-1 table-content-center hidden-xs-down',
      },
      {
        key: 'status',
        labelId: 'field.status',
        field: 'status',
        sortable: false,
        className: 'table-content-1 table-content-center',
      },
    ],

    // Search fields
    searchFields: [],
  }),
};

module.exports = planning;



// WEBPACK FOOTER //
// ./models/planning.js